import { firmIntelligenceUrl } from 'common/config/urls';
import { isDashboardDefinition } from 'common/types/summaryDefinitions';
import type { TertiaryNavigationLinkItem } from 'context/FirmBaseLayoutContext';
import type { OrderedDefinition } from 'context/SummaryDefinitionsContext';
import {
  INTELLIGENCE_CUSTOM_VIEW_SLUG_PREFIX,
  INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS,
  INTELLIGENCE_TERTIARY_NAVIGATION_LABELS,
  INTELLIGENCE_TERTIARY_NAVIGATION_SLUGS,
} from './intelligenceNavigation.constants';

export const getCustomViewSlug = (definitionId: number) =>
  `${INTELLIGENCE_CUSTOM_VIEW_SLUG_PREFIX}${definitionId}`;

export const parseCustomViewSlug = (slug: string | undefined): number | null => {
  if (!slug || !slug.startsWith(INTELLIGENCE_CUSTOM_VIEW_SLUG_PREFIX)) {
    return null;
  }
  const parsed = Number(slug.slice(INTELLIGENCE_CUSTOM_VIEW_SLUG_PREFIX.length));
  return Number.isInteger(parsed) ? parsed : null;
};

export const getDefaultFirmSummaryDefinition = (
  orderedDefinitions: OrderedDefinition[]
): OrderedDefinition | null => {
  // Prefer the system global Firm Summary when present; otherwise fall back to whichever
  // definition lives at the head of the list (e.g. an auto-seeded firm-owned default).
  // Dashboards are excluded from the fallback: the fixed Firm Summary tab always renders the
  // spreadsheet body, and getCustomFirmDefinitions filters out whatever this returns, so a
  // dashboard picked up here would also lose its own tab.
  const global = orderedDefinitions.find(definition => definition.isGlobal);
  return global ?? orderedDefinitions.find(definition => !isDashboardDefinition(definition)) ?? null;
};

export const getCustomFirmDefinitions = (
  orderedDefinitions: OrderedDefinition[]
): OrderedDefinition[] => {
  const defaultDefinition = getDefaultFirmSummaryDefinition(orderedDefinitions);
  return orderedDefinitions.filter(definition => definition.id !== defaultDefinition?.id);
};

const defaultBaseUrlFn = (slug: string) =>
  `${firmIntelligenceUrl(slug)}/${INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS.SUMMARIES}`;

export const buildIntelligenceTertiaryNavigationItems = (
  firmSlug: string,
  orderedDefinitions: OrderedDefinition[],
  search = '',
  baseUrlFn: (slug: string) => string = defaultBaseUrlFn,
  navigationSlugs: { FIRM_SUMMARY: string } = INTELLIGENCE_TERTIARY_NAVIGATION_SLUGS,
  navigationLabels: { FIRM_SUMMARY: string } = INTELLIGENCE_TERTIARY_NAVIGATION_LABELS
): TertiaryNavigationLinkItem[] => {
  const baseUrl = baseUrlFn(firmSlug);
  // Preserve sticky params like ?fund= when the user switches views.
  let trailingSearch = '';
  if (search) {
    trailingSearch = search.startsWith('?') ? search : `?${search}`;
  }

  const firmSummaryItem: TertiaryNavigationLinkItem = {
    label: navigationLabels.FIRM_SUMMARY,
    showMenu: true,
    value: navigationSlugs.FIRM_SUMMARY,
    to: `${baseUrl}/${navigationSlugs.FIRM_SUMMARY}${trailingSearch}`,
  };

  const customItems: TertiaryNavigationLinkItem[] = getCustomFirmDefinitions(orderedDefinitions).map(definition => {
    const slug = getCustomViewSlug(definition.id);
    return {
      label: definition.name,
      showMenu: true,
      value: slug,
      to: `${baseUrl}/${slug}${trailingSearch}`,
    };
  });

  return [firmSummaryItem, ...customItems];
};
